import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { qk } from '../api/queries'
import type { ApiKey, ApiKeysResponse, ApiKeyWithSecret } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { CopyButton } from '../components/CopyButton'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

function CreateKeyModal({ onClose }: { onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const create = useMutation({
    mutationFn: (n: string) =>
      apiFetch<ApiKeyWithSecret>(`/workspaces/${workspaceId}/api-keys`, {
        method: 'POST',
        body: JSON.stringify({ name: n }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys(workspaceId) }),
  })

  const created = create.data
  if (created) {
    return (
      <Modal
        title="API key created"
        onClose={onClose}
        footer={<button type="button" className="btn btn--primary" onClick={onClose}>Done</button>}
      >
        <p className="warn-note">
          Copy this key now. For your security it will <strong>not</strong> be shown again.
        </p>
        <div className="secret-row">
          <code className="secret" aria-label="New API key">{created.key}</code>
          <CopyButton value={created.key} />
        </div>
      </Modal>
    )
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) create.mutate(name.trim())
  }

  return (
    <Modal title="Create API key" onClose={onClose}>
      <form onSubmit={onSubmit} aria-label="Create API key form">
        <div className="field">
          <label htmlFor="key-name">Name</label>
          <input
            id="key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. production-agent"
            maxLength={100}
          />
        </div>
        {create.error && (
          <p className="form-error" role="alert">
            {isApiError(create.error) && create.error.status === 403
              ? 'Your role can’t create API keys in this workspace.'
              : 'Couldn’t create the key. Try again.'}
          </p>
        )}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function RevokeModal({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const revoke = useMutation({
    mutationFn: () =>
      apiFetch(`/workspaces/${workspaceId}/api-keys/${apiKey.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.apiKeys(workspaceId) })
      onClose()
    },
  })
  return (
    <Modal
      title="Revoke API key"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => revoke.mutate()}
            disabled={revoke.isPending}
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke key'}
          </button>
        </>
      }
    >
      <p>
        Revoke <strong>{apiKey.name}</strong>? Any agent or app using it will stop working
        immediately. This can’t be undone.
      </p>
      {revoke.error && <p className="form-error" role="alert">Couldn’t revoke the key.</p>}
    </Modal>
  )
}

export function ApiKeysView() {
  const { workspaceId } = useSession()
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<ApiKey | null>(null)
  const q = useQuery({
    queryKey: qk.apiKeys(workspaceId),
    queryFn: () => apiFetch<ApiKeysResponse>(`/workspaces/${workspaceId}/api-keys`),
    enabled: !!workspaceId,
  })
  const keys = q.data?.api_keys ?? []
  const forbidden = isApiError(q.error) && q.error.status === 403

  return (
    <div className="page">
      <PageHeader
        title="API Keys"
        count={q.data ? keys.length : undefined}
        description="Keys authenticate your agents and apps against this workspace’s MCP endpoint and REST API."
        actions={
          !forbidden && (
            <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} /> Create API Key
            </button>
          )
        }
      />

      <QueryState isLoading={q.isLoading} error={q.error} what="API keys" onRetry={() => q.refetch()}>
        {keys.length === 0 ? (
          <EmptyState
            icon="key"
            title="No API keys yet"
            body="Create a key to connect an agent, the SDK or an MCP client to this workspace."
            action={
              <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
                <Icon name="plus" size={14} /> Create API Key
              </button>
            }
            sampleHeaders={['Name', 'Fingerprint', 'Last used by', 'Created']}
            sampleRows={[
              ['production-agent', <code>sk_…9f2a</code>, 'claude-desktop 1.4', 'Oct 12, 2026'],
              ['staging', <code>sk_…41c0</code>, '—', 'Sep 30, 2026'],
            ]}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Fingerprint</th>
                  <th>Last used by</th>
                  <th>Created</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div className="cell-strong">{k.name}</div>
                      {k.description && <div className="cell-sub">{k.description}</div>}
                    </td>
                    <td><code>{k.fingerprint}</code></td>
                    <td>
                      {k.last_client_name
                        ? `${k.last_client_name}${k.last_client_version ? ` ${k.last_client_version}` : ''}`
                        : <span className="muted">Never used</span>}
                    </td>
                    <td>{formatDate(k.created_at)}</td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost-danger"
                        onClick={() => setRevoking(k)}
                        aria-label={`Revoke ${k.name}`}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>

      <div className="card-grid">
        <section className="card card--soon">
          <div className="card-head"><h2>Scoped keys</h2><Badge tone="info">Coming soon</Badge></div>
          <p>Limit a key to specific toolkits, tools or end users so a leaked key has a small blast radius.</p>
        </section>
        <section className="card card--soon">
          <div className="card-head"><h2>IP allowlist</h2><Badge tone="info">Coming soon</Badge></div>
          <p>Only accept requests made with this workspace’s keys from the IP ranges you trust.</p>
        </section>
      </div>

      {creating && <CreateKeyModal onClose={() => setCreating(false)} />}
      {revoking && <RevokeModal apiKey={revoking} onClose={() => setRevoking(null)} />}
    </div>
  )
}
